import TailwindSideModal from "../../../components/commonModal/TailwindSideModal";
import TailwindBottomButtonView from "../../../components/mainBottomButtonView/TailwindBottomButtonView";
import { Badge } from "../../../components/ui/common/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../components/ui/common/select";
import { cn } from "../../../lib/utils";
import LoadingOverlay from "../productList/components/LoadingOverlay";
import EnvironmentalFootprintTab from "./components/tabs/EnvironmentalFootprintTab";
import HistoryTab from "./components/tabs/HistoryTab";
import SummaryTab from "./components/tabs/SummaryTab";
import { useProductDetailsViewModel } from "./hooks/useProductDetailsViewModel";

interface ProductDetailModalProps {
  visibleModal: boolean;
  setVisibleModal: (visible: boolean) => void;
  productId: string;
  openEditDetails: () => void;
}

const TABS = [
  { key: "0", label: "Product Summary" },
  { key: "1", label: "Environmental Footprint" },
  { key: "2", label: "History" },
];

/**
 * Tailwind rebuild of the legacy `ProductDetailModal`
 * (src/screens/product/productDetails/ProductDetailsModal.tsx) — same
 * 4-tab read-only summary/impact/history/volume-and-cost layout.
 *
 * Header and tabs match the Lovable prototype's `ProductDetailDialog`
 * exactly: the type badge + register number under the title, the sales-unit
 * selector in the header's right slot (via `TailwindSideModal`'s
 * `titleMeta`/`headerRight`), and a hand-rolled underline tab bar rather
 * than the shared `common/tabs.tsx` (that component is also used by
 * `ImpactCardsGrid.tsx` elsewhere in the app with its own pill-tab look —
 * restyling it there would've been an unrelated, out-of-scope visual
 * change, so this modal owns its own tab bar instead, same as the
 * prototype does).
 */
const ProductDetailModal = ({
  visibleModal,
  setVisibleModal,
  productId,
  openEditDetails
}: ProductDetailModalProps) => {
  const vm = useProductDetailsViewModel({
    productId,
    setVisibleModal,
    navigateToEdit: openEditDetails
  });

  return (
    <>
      <TailwindSideModal
        open={visibleModal}
        onOpenChange={open => {
          if (!open) vm.onClose();
        }}
        title={vm.productDetails?.product_name || "Product Details"}
        titleClassName="tw-font-display tw-truncate"
        titleMeta={
          <div className="tw-flex tw-items-center tw-gap-2">
            <Badge variant="outline" className="tw-font-mono tw-text-[10px]">
              {vm.productDetails?.product_type || "-"}
            </Badge>
            <span className="tw-font-mono tw-text-xs tw-text-muted-foreground">
              {vm.productDetails?.registration_number || "no register number"}
            </span>
          </div>
        }
        headerRight={
          <>
            <div>
              <p className="tw-mb-1 tw-text-[10px] tw-font-medium tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                Sales unit
              </p>
              <Select value={vm.defaultSalesUnit} onValueChange={vm.onChangeSalesUnit}>
                <SelectTrigger className="!tw-h-9 tw-w-[200px] tw-text-sm">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {vm.salesUnitOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.text}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <span className="tw-w-4" />
          </>
        }
      >
        {/* Hand-rolled flex layout instead of `CommonSplitContainer`: that
            component sizes its scrollable area as `windowHeight * currentHeight
            - 5rem`, a fixed 80px allowance for the header — which assumes a
            single-line header. This modal's header is taller (title +
            type/register-number row + the sales-unit selector via
            `titleMeta`/`headerRight`), so that allowance came up short and
            pushed the bottom button bar below the fold. A plain flex column
            (`SheetContent` is already `flex flex-col h-full`) sizes the
            scroll area to whatever's actually left after the real header and
            footer heights, so it's correct regardless of header height or
            window size. */}
        <div className="tw-flex tw-flex-1 tw-min-h-0 tw-flex-col">
          <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-overflow-x-hidden">
            <div
              role="tablist"
              aria-label="Product detail tabs"
              className="tw-sticky tw-top-0 tw-z-10 tw-flex tw-items-center tw-gap-1 tw-overflow-x-auto tw-border-b tw-border-border tw-bg-white tw-px-6"
            >
              {TABS.map(tab => {
                const isActive = String(vm.selectedTab) === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => vm.onSelectTab(Number(tab.key))}
                    className={cn(
                      "-tw-mb-px tw-whitespace-nowrap tw-border-b-2 tw-px-3 tw-py-2.5 tw-text-sm tw-transition-colors",
                      isActive
                        ? "tw-border-primary tw-font-semibold tw-text-primary"
                        : "tw-border-transparent tw-text-muted-foreground hover:tw-text-foreground"
                    )}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div className="tw-bg-muted/20 tw-p-6">
              {vm.selectedTab === 0 ? <SummaryTab vm={vm} /> : null}
              {vm.selectedTab === 1 ? <EnvironmentalFootprintTab vm={vm} /> : null}
              {vm.selectedTab === 2 ? <HistoryTab vm={vm} /> : null}
            </div>
          </div>

          <div className="tw-shrink-0">
            <TailwindBottomButtonView
              cancelButtonStatus
              saveButtonStatus
              saveTitle="Edit"
              buttonSize="lg"
              cancelButton={vm.onClose}
              saveButton={vm.onEdit}
            />
          </div>
        </div>
      </TailwindSideModal>

      {vm.isRowsLoading ? <LoadingOverlay /> : null}
    </>
  );
};

export default ProductDetailModal;
